import { ChevronDown, Search } from "lucide-react";

import { Input } from "@/app/admin/_common/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/app/admin/_common/ui/dropdown-menu";

import { ALL } from "../automationsPageModel";
import { useAutomationSchema } from "./useAutomationSchema";
import { STATUS_LABEL } from "./automationStatus";

function FilterDropdown({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}) {
  const activeLabel = options.find((o) => o.value === value)?.label ?? label;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex h-[30px] items-center gap-1.5 rounded-md border border-input bg-white px-3 text-[12.5px] text-foreground hover:bg-muted"
        >
          <span className="hidden text-muted-foreground sm:inline">{label}:</span>
          {activeLabel}
          <ChevronDown className="size-3.5 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuRadioGroup value={value} onValueChange={onChange}>
          {options.map((option) => (
            <DropdownMenuRadioItem key={option.value} value={option.value}>
              {option.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function AutomationsToolbar({
  search,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  eventFilter,
  onEventFilterChange,
  usedEventKeys,
}: {
  search: string;
  onSearchChange: (value: string) => void;
  statusFilter: string;
  onStatusFilterChange: (value: string) => void;
  eventFilter: string;
  onEventFilterChange: (value: string) => void;
  usedEventKeys: string[];
}) {
  const { schema } = useAutomationSchema();

  const statusOptions = [
    { value: ALL, label: "All" },
    ...Object.entries(STATUS_LABEL).map(([value, label]) => ({ value, label })),
  ];

  // Only events that some automation actually uses, so the filter cannot offer a choice
  // that matches nothing.
  const eventOptions = [
    { value: ALL, label: "All" },
    ...usedEventKeys.map((key) => ({
      value: key,
      label: schema?.events.find((e) => e.key === key)?.label ?? key,
    })),
  ];

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative max-w-[420px] flex-1">
        <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
          <Search className="size-3.5 text-muted-foreground" />
        </div>
        <Input
          type="search"
          placeholder="Search name or trigger…"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="h-[30px] pl-9 text-[12.5px]"
          aria-label="Search automations"
        />
      </div>

      <FilterDropdown
        label="Status"
        value={statusFilter}
        onChange={onStatusFilterChange}
        options={statusOptions}
      />

      <FilterDropdown
        label="Trigger"
        value={eventFilter}
        onChange={onEventFilterChange}
        options={eventOptions}
      />
    </div>
  );
}
